import { FilterInput } from '@apeiron/common'
import { SlotType, StarFilterInput } from '@apeiron/star'
import { styled } from '@mui/material'
import BlurForm from '@src/components/share/filter/BlurForm'
import {
  FilterSection,
  FilterSectionContent,
  FilterSectionHeader,
  FilterSectionTitle
} from '@src/components/share/filter/FilterSection'
import RangeSlider from '@src/components/share/filter/RangeSlider'
import ResetFilter from '@src/components/share/filter/ResetFilter'
import useFetchStarFilterJson from '@src/hooks/json/useFetchStarFilterJson'
import * as R from 'ramda'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

const FILTER_FIELDS: (keyof StarFilterInput)[] = [
  'asteroidBeltSlotsMax',
  'asteroidBeltSlotsMin',
  'cometSlotsMax',
  'cometSlotsMin',
  'kuiperBeltSlotsMax',
  'kuiperBeltSlotsMin',
  'sunSlotsMax',
  'sunSlotsMin',
  'supernovaSlotsMax',
  'supernovaSlotsMin',
  'agingBuffMax',
  'agingBuffMin',
  'orbitalTrackCountMax',
  'orbitalTrackCountMin'
]

const Content = styled(FilterSectionContent)`
  &.MuiAccordionDetails-root {
    padding: 10px 10px 10px 24px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    gap: 15px;
  }
`

const Form = styled(BlurForm)`
  > * + * {
    margin-top: 16px;
  }
`

const StarSlotFilter: FC<Props> = (props: Props) => {
  const { className, filterInput, onFilterChange } = props

  const { t } = useTranslation()

  const [slotsData, setSlotsData] = useState<SlotType[]>([])

  const { data } = useFetchStarFilterJson()

  const numberOfFilters = useMemo(() => {
    const countFilters = R.pipe<any, any, Object, unknown[][], number>(
      R.reject(R.isNil),
      R.pick(FILTER_FIELDS),
      R.toPairs,
      R.length
    )

    const numberOfSlotFilters = countFilters(filterInput)

    return numberOfSlotFilters
  }, [filterInput])

  const handleOnReset = useCallback(() => {
    onFilterChange(R.omit(FILTER_FIELDS, filterInput))
  }, [filterInput, onFilterChange])

  const handleOnValueChange = useCallback(
    (filter: FilterInput) => {
      onFilterChange(filter as StarFilterInput)
    },
    [onFilterChange]
  )

  useEffect(() => {
    setSlotsData(R.propOr([], 'starFilterSlots', data))
  }, [data])

  return (
    <FilterSection className={className} defaultExpanded>
      <FilterSectionHeader>
        <FilterSectionTitle>
          {t('star.list.filter.astronomical_slot.title')}
        </FilterSectionTitle>
        <ResetFilter filterCount={numberOfFilters} onReset={handleOnReset} />
      </FilterSectionHeader>
      <Content>
        {!R.isEmpty(slotsData) && (
          <Form>
            {slotsData.map((slotData: SlotType) => (
              <RangeSlider
                decimalScale={slotData.decimalScale}
                filterOptions={filterInput}
                key={slotData.name}
                labelIconURL={slotData.image}
                labelText={slotData.name}
                max={slotData.max.slot}
                maxValueKey={slotData.max.field}
                min={slotData.min.slot}
                minValueKey={slotData.min.field}
                onFilterOptionsChange={handleOnValueChange}
              />
            ))}
          </Form>
        )}
      </Content>
    </FilterSection>
  )
}

type Props = {
  className?: string
  filterInput: StarFilterInput
  onFilterChange: (input: StarFilterInput) => void
}

export default StarSlotFilter
